import React, { useEffect, useMemo, useRef, useState } from 'react'

import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import FormListForMyCustomForView from './FormListForMyCustomForView'

import { AgentModals } from '../../../functions/agent/AgentModals'
import { type RootState } from '../../../store/store'
import { useAppDispatch, useAppSelector } from '../../../hooks/redux'

import ChildViewing from './ChildViewing'
import ChildUnit from './ChildUnit'
import fillOfferModal from '../../../functions/agent/modals/fillOfferModal'
import ChildOffer from './ChildOffer'
import ChildAdvertise from './ChildAdvertise'
import {
    showInputAlert,
    swalAttentionLeave, swalError,
    swalSuccess
} from '../../../functions/swalOptions'
import { type IClient, type User } from '../../../store/slices/userSlice'
import { type IAdvertise } from '../../../store/slices/advertisesSlice'

import { type IViewing } from '../../../store/slices/viewingsSlice'
import useUserType from '../../../hooks/UseUserType'

import {
    areUnitActionButtonsBlocked
} from '../../../functions/shared/units/getUnitOfferStatus'
import { type IOffer } from '../../../store/slices/offersSlice'
import ImagesPreviewGroup from './previewImage/ImagesPreviewGroup'
import _ from 'lodash'
import CloseIconBasic from '../../../components/shared/elements/CloseIconBasic'
import VideoForMyCustomModalForView from './unitsActions/VideoForMyCustomModalForView'
import FloorPlanForMyCustomModalForView from './unitsActions/FloorPlanForMyCustomModalForView'
import { toUTCWithOffset } from '../../../functions/agent/date/toUTCWithOffset'
import { sendNotification } from '../../../functions/shared/notifications/sendNotification'
import { openChatByUserID } from '../../../store/actionsCreators/chatActions'
import ChildBroadcast from './ChildBroadcast'
import GeoForMyCustomModalForView from './unitsActions/GeoForMyCustomModalForView'
import { isPinnedChat } from '../../../components/shared/newChat/chatList'
import { getBelongsUnitForAdvertise, getBelongsUnitForOffer, getBelongsUnitForViewing } from '../../../functions/shared/schedule/getBelongsUnit'
import TakeUnitsPortfolio from './unitsActions/TakeUnitsPortfolio'
import { sendUnitNotification } from '../../../store/actionsCreators/unitsActions'
import { getCurrentDubaiDate } from '../../../functions/agent/date/getCurrentDubaiDate'
import RegulatoryInformation from './RegulatoryInformation'
import AgentInfoForChildren from './contactInfo/AgentInfoForChildren'
import { fetchAdvertisesForUnitForAdmin } from '../../../store/actionsCreators/userActions/fetchAdvertisesForUnitForAdmin'
import { manageVerifiedRejectAgentUnit } from '../../../store/actionsCreators/unitActions/manageVerifiedRejectAgentUnit'
import { setWhereAfterToGo } from '../../../store/slices/modalSlice'
import CustomModalForViewRenderDescription from './CustomModalForViewRenderDescription'
import VerifiedUnitInformation from './VerifiedUnitInformation'
import useMediaQuery from '../../../hooks/useMediaQuery'
import UnitVerificationReviewPanel from './UnitVerificationReviewPanel'
import { fetchUserById } from '../../../store/actionsCreators/userActions'
import { hydrateUnitLandlords } from '../../../store/actionsCreators/ownersActions'
import AdvertiseParticipation from './unitsActions/AdvertiseParticipation'
import { updateFindingTenantOrderDates } from '../../../components/adminModule/clientOrders/orderRelatedServiceUpdates'
import { isFindingTenantParentOrder } from './findingTenantRelatedServices'

const actionRoutes: Record<string, string> = {
    magic_viewing_modal: 'viewing_actions',
    magic_offer_modal: 'offer_actions',
    magic_advertise_modal: 'advertise_actions'
}

export default function MyCustomModalForView(): JSX.Element {
    const navigate = useNavigate()
    const locationPath = useLocation().pathname
    const dispatch = useAppDispatch()
    const [searchParams] = useSearchParams()
    const { modalID, unitID, objectID, from } = useParams()
    // params
    const isOtherAgentUnit = from === 'other_agent_s_unit'
    const isMyUnit = from === 'my_unit'
    const isPublishedUnitAdminFlow = from === 'published_unit_admin'


    const isDesktop = useMediaQuery('(min-width: 992px)');
    const isMobileCanvas = useMediaQuery('(max-width: 480px)');

    // basic states
    const currentUser = useAppSelector((state: RootState) => state.user.currentUser) as User

    const offers = useAppSelector((state: RootState) => state.offers.offers)
    const advertises = useAppSelector((state: RootState) => state.advertises.advertises)
    const units = useAppSelector((state: RootState) => state.units.units)
    const viewings = useAppSelector((state: RootState) => state.viewings.viewings)
    const allUsers = useAppSelector((state: RootState) => state.user.allUsers)
    // extra flags for modal
    const { userType, userTypeLowercase, basicUserType } = useUserType()
    const userDevice = useAppSelector((state) => state.app.userDevice)
    const loading = useAppSelector(state => state.modal.loading)
    const satedRedirect = useAppSelector((state: RootState) => state.modal.whereAfterToGo)


    const clients: IClient[] = currentUser?.Clients // useAppSelector((state: RootState) => (state.user.currentUser as User)?.Clients)

    // current collections in this modal
    const currentUnit = useMemo(() => units?.find((el: any) => el.id === Number(unitID)), [units, unitID])
    const getOrderUnitID = (order: any): number | null => {
        const unitRef = order?.Unit
        if (typeof unitRef === 'number') return unitRef
        if (typeof unitRef === 'string' && !Number.isNaN(Number(unitRef))) return Number(unitRef)
        if (unitRef?.id != null) return Number(unitRef.id)
        if (unitRef?.data?.id != null) return Number(unitRef.data.id)
        return null
    }
    const normalizeServiceToken = (value: unknown): string => String(value ?? '')
        .trim()
        .toLowerCase()
        .replace(/^services\./, '')
        .replace(/[\s-]+/g, '_')
    const isFindingTenantService = (service: any): boolean => {
        if (service?.__component === 'services.finding-tenant') return true
        const raw = service?.serviceId ?? service?.ServiceId ?? service?.ServiceID ?? service?.id ?? service?.Type ?? service?.Name ?? service?.Package ?? service?.__component
        const canonical = normalizeServiceToken(raw)
        return canonical === 'find_tenant' || canonical === 'finding_tenant'
    }
    const getFindingTenantServices = (order: any): any[] => (
        Array.isArray(order?.Service) ? order.Service.filter((service: any) => isFindingTenantService(service)) : []
    )
    const isFindingTenantOrder = (order: any): boolean => (
        isFindingTenantParentOrder(order) || getFindingTenantServices(order).length > 0
    )
    const FINDING_TENANT_DURATION_BY_PACKAGE: Array<{ label: string, days: number }> = [
        { label: 'Thirty Days Plan', days: 30 },
        { label: '30 Days Plan', days: 30 },
        { label: 'Forty Five Days Plan', days: 45 },
        { label: '45 Days Plan', days: 45 },
        { label: 'Sixty Days Plan', days: 60 },
        { label: '60 Days Plan', days: 60 }
    ]
    const normalizePackageLabel = (value: unknown): string => String(value ?? '')
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, ' ')
        .replace(/\s+/g, ' ')
    const normalizeStatusToken = (value: unknown): string => String(value ?? '').trim().toLowerCase()
    const FINDING_TENANT_TERMINAL_STATUS_TOKENS = ['canceled', 'cancelled', 'completed', 'rejected', 'expired', 'failed']
    const isTerminalFindingTenantOrderStatus = (value: unknown): boolean => {
        const normalized = normalizeStatusToken(value)
        if (normalized.length === 0) return false
        return FINDING_TENANT_TERMINAL_STATUS_TOKENS.some((token) => normalized === token || normalized.includes(token))
    }
    const getFindingTenantOrderStatusCandidates = (order: any): string[] => {
        const rootStatuses = Array.isArray(order?.Statuses) ? order.Statuses : []
        const topLevel = [order?.Status, order?.ServiceStatus]
        const findingTenantStatuses = getFindingTenantServices(order).flatMap((service: any) => (
            [service?.Status, service?.ServiceStatus, service?.Statuses?.[0]]
        ))
        return [...rootStatuses, ...topLevel, ...findingTenantStatuses]
            .map((status) => String(status ?? '').trim())
            .filter((status) => status.length > 0)
    }
    const isActiveFindingTenantOrder = (order: any): boolean => {
        const statuses = getFindingTenantOrderStatusCandidates(order)
        if (statuses.length === 0) return true
        return !statuses.some((status) => isTerminalFindingTenantOrderStatus(status))
    }
    const isUnpaidOrder = (order: any): boolean => {
        return Array.isArray(order?.Statuses) && order.Statuses.includes("Unpaid")
    }
    const getOrderTimestampForSort = (order: any): number => {
        const updated = Date.parse(String(order?.updatedAt ?? ''))
        if (Number.isFinite(updated)) return updated
        const created = Date.parse(String(order?.createdAt ?? ''))
        if (Number.isFinite(created)) return created
        const idAsNumber = Number(order?.id)
        return Number.isFinite(idAsNumber) ? idAsNumber : 0
    }
    const resolveCurrentFindingTenantOrder = (orders: any[], targetUnitId: number, allowTerminalFallback = false): any | undefined => {
        const linkedOrders = orders.filter((order: any) => {
            const linkedUnitId = getOrderUnitID(order)
            return linkedUnitId === targetUnitId && isFindingTenantOrder(order) && !isUnpaidOrder(order)
        })
        if (linkedOrders.length === 0) return undefined

        const activeOrders = linkedOrders.filter((order: any) => isActiveFindingTenantOrder(order))
        if (activeOrders.length === 0 && !allowTerminalFallback) return undefined
        const candidateOrders = activeOrders.length > 0 ? activeOrders : linkedOrders
        const sorted = [...candidateOrders].sort((a, b) => {
            const timeDiff = getOrderTimestampForSort(b) - getOrderTimestampForSort(a)
            if (timeDiff !== 0) return timeDiff
            const idDiff = Number(b?.id ?? 0) - Number(a?.id ?? 0)
            if (idDiff !== 0) return idDiff
            return String(b?.id ?? '').localeCompare(String(a?.id ?? ''))
        })
        return sorted[0]
    }
    const getFindingTenantDurationDays = (order: any): number => {
        const legacyFindingTenantService = getFindingTenantServices(order)[0]
        const numericCandidates = [
            order?.DurationDays,
            order?.durationDays,
            legacyFindingTenantService?.DurationDays,
            legacyFindingTenantService?.durationDays,
        ]
        for (const value of numericCandidates) {
            const parsed = Number(value)
            if (Number.isFinite(parsed) && parsed > 0) return parsed
        }

        const packageName = String(order?.Package ?? legacyFindingTenantService?.Package ?? '').trim()
        if (packageName.length > 0) {
            const normalizedPackage = normalizePackageLabel(packageName)
            const matched = FINDING_TENANT_DURATION_BY_PACKAGE.find((item) => normalizePackageLabel(item.label) === normalizedPackage)
            if (matched != null) return matched.days
            if (process.env.REACT_APP_IS_DEV === 'true') {
                console.warn('[publish landlord unit] Unrecognized Finding Tenant package, using 30-day fallback', packageName)
            }
        } else if (process.env.REACT_APP_IS_DEV === 'true') {
            console.warn('[publish landlord unit] Missing Finding Tenant package, using 30-day fallback')
        }

        return 30
    }
    const getOrderId = (order: any): number | null => {
        const parsed = Number(order?.id)
        return Number.isFinite(parsed) && parsed > 0 ? parsed : null
    }
    const updateFindingTenantServiceDatesOnPublish = async (publishDate: Date): Promise<void> => {
        const isLandlordUnit = currentUnit?.attributes?.ManagedBy === 'Landlord'
        if (!isLandlordUnit) return

        const order = resolveCurrentFindingTenantOrder(relatedOrdersForCurrentUnit, Number(unitID))
        if (order == null) {
            if (process.env.REACT_APP_IS_DEV === 'true') console.warn('[publish landlord unit] Active Finding Tenant order was not found for unit', unitID)
            return
        }

        const orderId = getOrderId(order)
        if (orderId == null) {
            if (process.env.REACT_APP_IS_DEV === 'true') console.warn('[publish landlord unit] Finding Tenant order id is missing', order)
            return
        }

        const durationDays = getFindingTenantDurationDays(order)

        const serviceStartDate = publishDate.toISOString()
        const serviceEndDate = new Date(publishDate.getTime())
        serviceEndDate.setDate(serviceEndDate.getDate() + durationDays)
        await updateFindingTenantOrderDates({
            order,
            patch: {
                ServiceStartDate: serviceStartDate,
                ServiceEndDate: serviceEndDate.toISOString(),
            },
        })
    }
    const ownerOrdersCacheRef = useRef<Record<number, any[]>>({})
    const ownerUsersCacheRef = useRef<Record<number, any>>({})
    const [verificationUnit, setVerificationUnit] = useState<any>(currentUnit)
    const [relatedOrdersForCurrentUnit, setRelatedOrdersForCurrentUnit] = useState<any[]>([])

    useEffect(() => {
        let cancelled = false

        const hydrateVerificationData = async (): Promise<void> => {
            if (currentUnit == null) {
                if (!cancelled) {
                    setVerificationUnit(undefined)
                    setRelatedOrdersForCurrentUnit([])
                }
                return
            }

            let nextUnit: any = currentUnit
            const landlords = currentUnit?.attributes?.Landlords ?? []
            const needHydrateLandlords = Array.isArray(landlords) && landlords.some((relation: any) => {
                const landlordRef = relation?.Landlord
                const landlordEntity = landlordRef?.data ?? landlordRef
                return landlordEntity?.id != null && landlordEntity?.ContactInfo == null
            })

            if (needHydrateLandlords) {
                try {
                    const hydratedAttributes = await hydrateUnitLandlords({
                        Landlords: landlords
                    })
                    nextUnit = {
                        ...currentUnit,
                        attributes: {
                            ...currentUnit.attributes,
                            Landlords: hydratedAttributes?.Landlords ?? landlords
                        }
                    }
                } catch (error) {
                    console.error('Failed to hydrate unit landlords for verification', error)
                }
            }

            const unitOwnerID = currentUnit?.attributes?.User?.data?.id
            let ordersSource: any[] = []
            let selectedOwnerUser: any = null

            if (unitOwnerID != null) {
                if (Number(unitOwnerID) === Number(currentUser?.id)) {
                    ordersSource = (currentUser?.Orders ?? []) as any[]
                    selectedOwnerUser = currentUser
                } else {
                    const cachedOrders = ownerOrdersCacheRef.current[Number(unitOwnerID)]
                    const cachedOwner = ownerUsersCacheRef.current[Number(unitOwnerID)]
                    if (cachedOrders != null) {
                        ordersSource = cachedOrders
                        selectedOwnerUser = cachedOwner ?? null
                    } else {
                        const fetchedOwner = await dispatch(fetchUserById(Number(unitOwnerID)))
                        const fetchedOrders = (fetchedOwner?.Orders ?? []) as any[]
                        ownerOrdersCacheRef.current[Number(unitOwnerID)] = fetchedOrders
                        if (fetchedOwner != null) ownerUsersCacheRef.current[Number(unitOwnerID)] = fetchedOwner
                        ordersSource = fetchedOrders
                        selectedOwnerUser = fetchedOwner
                    }
                }
            }

            if (selectedOwnerUser != null) {
                nextUnit = {
                    ...nextUnit,
                    attributes: {
                        ...nextUnit.attributes,
                        NocOwnerUser: selectedOwnerUser
                    }
                }
            }

            const filteredOrders = ordersSource.filter((order: any) => getOrderUnitID(order) === Number(unitID))

            if (!cancelled) {
                setVerificationUnit(nextUnit)
                setRelatedOrdersForCurrentUnit(filteredOrders)
            }
        }

        void hydrateVerificationData()

        return () => {
            cancelled = true
        }
    }, [currentUnit, currentUser?.Orders, currentUser?.id, dispatch, unitID])

    const currentOfferRaw = useMemo(() => offers?.find((el: any) => el.id === Number(objectID)), [offers, objectID])
    const currentOffer: any = useMemo(
        () => ({ id: currentOfferRaw?.id, ...currentOfferRaw?.attributes }),
        [currentOfferRaw]
    ) // currentUser.Offers.find((el: any) => el.id === Number(objectID))
    const currentAdvertise = useMemo(
        () => advertises?.find((el: IAdvertise) => el.id === Number(objectID)),
        [advertises, objectID]
    )
    const chatList = useAppSelector(state => state.chatList.chatList)
    const suppChat = useMemo(() => chatList.find(isPinnedChat)?.userID, [chatList])



    // extra data

    const allModals = useMemo(() => AgentModals(), [currentUser?.id, userType])
    const modalItem = useMemo(() => allModals.filter((el: any) => el.id === modalID), [allModals, modalID])
    const unitModalForm = useMemo(() => {
        const form = allModals.find((el: any) => el.id === 'unit_modal')?.form ?? []
        if (basicUserType !== 'AdminBSO') return form
        const unitNumberIndex = form.findIndex((row: any) => row.id === 'Number')
        if (unitNumberIndex === -1 || form.some((row: any) => row.id === 'ERPUnitID')) return form
        return [
            ...form.slice(0, unitNumberIndex + 1),
            { id: 'ERPUnitID', content: ['ERP Unit ID'], element: 'label-secondary', icon: 'tag' },
            ...form.slice(unitNumberIndex + 1)
        ]
    }, [allModals, basicUserType])

    // viewings logic
    const currentViewing = useMemo(
        () => viewings?.filter((el: any) => el.id === Number(objectID)),
        [viewings, objectID]
    )

    //   { id: 'close', content: ['Close'], element: 'button-secondary', withIcon: true }
    // landlord / admin logic
    let modal: any
    let firstModal: any
    let dataSource: any
    let firstDataSource: any = null
    switch (modalID) {
        case 'unit_modal':
            dataSource = currentUnit
            if (userType === 'Landlord') {
                modal = [
                    {
                        ...modalItem?.[0],
                        footer: modalItem?.[0]?.footer
                            ?.filter((row: any) => row.id === 'close')
                            ?.map((row: any) => ({ ...row, withIcon: false }))
                    }
                ]
            } else {
                modal = modalItem
            }

            break
        case 'offer_modal':
        case 'magic_offer_modal':
            firstModal = fillOfferModal(_.cloneDeep(modalItem), +(objectID ?? '-1'), searchParams, dispatch, navigate, currentOffer)
            modal = [{
                ...modalItem[0],
                form: modalItem[0].form.filter(({ id }: { id: string }) => !['residential', 'commercial', 'sale', 'sale-commercial'].includes(id)),
                footer: firstModal?.[0]?.footer
            }]

            dataSource = currentUnit
            firstDataSource = currentOffer?.Type?.[0]

            break
        case 'client_modal':
            modal = modalItem
            dataSource = clients?.find((el: any) => el.id === Number(objectID))
            break
        default:
            dataSource = currentUnit
            modal = modalItem
    }

    const googleMapsLink = currentUnit?.attributes?.Property?.data?.attributes?.GoogleMapsLink ?? null





    const statusesViewing = currentViewing?.[0]?.attributes?.Statuses as string[]
    const statusesOffers = currentOfferRaw?.attributes?.Statuses as string[]


    const showButtonGoToActions =
        !(
            (modalID?.includes('offer_modal') && (statusesOffers?.includes('Canceled by other agent') || statusesOffers?.includes('Canceled') || statusesOffers?.includes('Completed'))) ||
            (modalID?.includes('viewing_modal') && statusesViewing?.includes('Completed'))
        ) && isMyUnit && userType !== 'Landlord';




    const [isShowFooter, setIsShowFooter] = useState(true)
    const showPopupViewingReschedule = useAppSelector((state: RootState) => state.swalsStates.showPopupViewingReschedule)
    const blockUnitActions = areUnitActionButtonsBlocked(currentUnit) && modal[0].id === 'unit_modal'


    useEffect(() => {
        if (showPopupViewingReschedule) {
            setIsShowFooter(false)
        } else {
            setIsShowFooter(true)
        }
    }, [showPopupViewingReschedule])

    const previewVisible = useAppSelector((state: RootState) => state.modal.previewVisible)
    const btnClose = modal?.[0]?.footer?.find((footerElement: any) => footerElement.id === 'close' || footerElement.element === 'button-secondary')
    if (locationPath?.includes('/agent/modalView/viewing_modal/') && from === "my_unit") {
        btnClose.redirect = '/agent/schedule'
    }







    const navigateToChatMain = (collection: any, forCollection: string): any => {
        let unit = null
        if (forCollection === "forOffers") {
            unit = getBelongsUnitForOffer(collection as IOffer)
        } if (forCollection === "forViewings") {
            unit = getBelongsUnitForViewing(collection as IViewing)
        } if (forCollection === "forAdvertise") {
            unit = getBelongsUnitForAdvertise(collection as IAdvertise)
        }
        const isUserTypeAgent = userType === 'Agent'

        let redirectUserID = null
        let redirectName = ""
        if (isUserTypeAgent && unit !== null) {
            if (unit.type === 'MY UNIT') {
                redirectUserID = collection?.attributes?.User?.data?.id
                redirectName = "Agent for details"
            } else if (unit.type === 'OTHER AGENT UNIT') {
                redirectUserID = collection?.attributes?.Unit?.data?.attributes?.User?.data?.id
                redirectName = "Agent for additional information"
            } else if (unit.type === 'BSO UNIT') {
                redirectUserID = suppChat
                redirectName = "BSO for additional information"
            }
        } else {
            if (unit !== null && unit.type === 'MY UNIT') {
                redirectUserID = collection?.attributes?.User?.data?.id
                redirectName = "Agent for details"
            } else {
                redirectUserID = suppChat
                redirectName = "BSO for additional information"
            }
        }
        return {
            redirectName,
            redirect: redirectUserID
        }

    }




    const isInRequest = currentUnit?.attributes?.Statuses?.includes('Registered')
    const currentUnitStatuses = currentUnit?.attributes?.Statuses ?? []
    const uniq = <T,>(arr: T[]) => Array.from(new Set(arr));
    const [isVerificationReviewReady, setIsVerificationReviewReady] = useState(false)
    const isReviewRequired = modalID === 'unit_modal' && isOtherAgentUnit && isInRequest
    const isLandlordVerificationFlow = currentUnit?.attributes?.ManagedBy === 'Landlord'
    const isAgentVerificationFlow = currentUnit?.attributes?.ManagedBy === 'Agent'
    const isLandlordPublished = currentUnit?.attributes?.ManagedBy === 'Landlord' && (currentUnit?.attributes?.Statuses ?? []).includes('Landlord Unit')
    const isLandlordApprovedOrVerified = isLandlordVerificationFlow &&
        (currentUnitStatuses.includes('Approved') || currentUnitStatuses.includes('Landlord Verified'))
    const isLandlordUnitReviewFlow = modalID === 'unit_modal' && isOtherAgentUnit && isLandlordVerificationFlow
    const isLandlordRegisteredForVerify = isLandlordUnitReviewFlow && currentUnitStatuses.includes('Registered')
    const isLandlordApprovedForPublish = isLandlordUnitReviewFlow && currentUnitStatuses.includes('Approved') && !currentUnitStatuses.includes('Landlord Unit')
    const isAgentRegisteredReviewFlow = modalID === 'unit_modal' && isOtherAgentUnit && isAgentVerificationFlow && isInRequest
    const isLandlordPostVerificationReviewFlow = modalID === 'unit_modal' &&
        isOtherAgentUnit &&
        isLandlordVerificationFlow &&
        (isLandlordPublished || isLandlordApprovedOrVerified)
    const isPublishedServiceManagementFlow = modalID === 'unit_modal' &&
        (
            (isPublishedUnitAdminFlow && (isLandlordVerificationFlow ? isLandlordPublished : !isInRequest)) ||
            isLandlordPostVerificationReviewFlow
        )

    const resolvePrimary = (managedBy?: string): 'Agent Unit' | 'Approved' =>
        managedBy === 'Landlord'
            ? 'Approved'
            : 'Agent Unit';

    const handleApproveRequestedUnit = async (): Promise<void> => {
        const unit = currentUnit
        if (unit == null) return
        await swalAttentionLeave("Are you shure <strong>to approve this unit </strong> in publish list? ").then(async (result: any) => {
            if (result.isConfirmed) {
                const managedBy = unit?.attributes?.ManagedBy as 'Agent' | 'Landlord' | undefined;
                const primary = resolvePrimary(managedBy);

                const current = unit?.attributes?.Statuses ?? [];
                const nextStatuses = uniq(current.map((s) => {
                    if (managedBy === 'Landlord') {
                        if (s === 'Registered') return 'Approved'
                        return s
                    }
                    return s === 'Registered' ? primary : s
                }));
                const currentPublished = current.includes('Landlord Unit') || current.includes('Agent Unit')
                const nextPublished = nextStatuses.includes('Landlord Unit') || nextStatuses.includes('Agent Unit')
                const shouldSendPublishedNotification = !currentPublished && nextPublished

                const { isSuccessful } = await dispatch(manageVerifiedRejectAgentUnit(
                    {
                        unitID: unit.id,
                        body: { Statuses: nextStatuses, CreateDate: new Date() }
                    }
                ))
                if (isSuccessful) {
                    const inMoment = toUTCWithOffset(getCurrentDubaiDate(), 1)
                    const isLandlordUnit = unit?.attributes?.ManagedBy === 'Landlord'

                    if (isLandlordUnit) {
                        await sendNotification(inMoment, unit?.attributes?.User?.data?.id ?? 0, unit?.id, 'user-unit-verification-success', 'user unit verification success')
                    } else {
                        const publishTimeEnded = toUTCWithOffset(new Date(unit.attributes.PublishEndDate as string), 1)
                        await sendNotification(inMoment, unit?.attributes?.User?.data?.id ?? 0, unit?.id, 'user-unit-approved', 'user unit approved')
                        await sendNotification(publishTimeEnded, unit?.attributes?.User?.data?.id ?? 0, unit?.id, 'user-unit-expired', 'user unit expired')

                    }
                    if (shouldSendPublishedNotification) {
                        void sendUnitNotification(unit, 'user-unit-new')
                    }
                }
            }
        }).catch(console.log);
    }

    const handlePublishApprovedLandlordUnit = async (): Promise<void> => {
        const unit = currentUnit
        if (unit == null) return
        await swalAttentionLeave("Are you shure <strong>to publish this unit</strong> to app catalog? ").then(async (result: any) => {
            if (result.isConfirmed) {
                const publishDate = new Date()
                const current = unit?.attributes?.Statuses ?? [];
                const nextStatuses = uniq(
                    current
                        .filter((s) => s !== 'Drafted')
                        .map((s) => (s === 'Approved' ? 'Landlord Unit' : s))
                );

                const { isSuccessful, textNotification } = await dispatch(manageVerifiedRejectAgentUnit(
                    {
                        unitID: unit.id,
                        body: { Statuses: nextStatuses, CreateDate: publishDate }
                    }
                ))

                if (isSuccessful) {
                    try {
                        await updateFindingTenantServiceDatesOnPublish(publishDate)
                    } catch (error) {
                        if (process.env.REACT_APP_IS_DEV === 'true') console.warn('[publish landlord unit] Finding Tenant date update failed', error)
                    }
                    const swalResult = await swalSuccess('Unit has been published successfully')
                    void sendUnitNotification(unit, 'user-unit-new')
                    if (swalResult.isConfirmed) {
                        navigate(`/landlord/modalView/unit_modal/${unit?.id}/not/other_agent_s_unit`)
                    }
                } else {
                    const swalResult = await swalError(textNotification)
                    if (swalResult.isConfirmed) {
                        navigate(`/landlord/modalView/unit_modal/${unit?.id}/not/other_agent_s_unit`)
                    }
                }
                dispatch(setWhereAfterToGo(`/landlord/my_units`))
            }
        }).catch(console.log);
    }

    const handleRejectRequestedUnit = async (): Promise<void> => {
        const unit = currentUnit
        if (unit == null) return
        await swalAttentionLeave("Are you shure <strong>to reject this unit</strong> to publish list? ").then(async (result: any) => {
            if (result.isConfirmed) {
                const current = unit?.attributes?.Statuses ?? [];
                const nextStatuses = uniq(
                    current.map(s => (s === 'Registered' ? 'Rejected' : s))
                );

                const popupResult = await showInputAlert('Write the reason for rejecting unit');
                if (popupResult.isConfirmed) {
                    const reason = popupResult?.value?.status;
                    const { isSuccessful, textNotification } = await dispatch(manageVerifiedRejectAgentUnit(
                        {
                            unitID: unit.id,
                            body: {
                                Statuses: nextStatuses,
                                PublishRejectionReason: reason ?? 'Rejected: reason not provided by the administrator.'
                            }
                        }
                    ))

                    if (isSuccessful) {
                        const inMoment = toUTCWithOffset(getCurrentDubaiDate(), 1)
                        const isLandlordUnit = unit?.attributes?.ManagedBy === 'Landlord'

                        if (isLandlordUnit) {
                            await sendNotification(inMoment, unit?.attributes.User?.data?.id ?? 0, unit?.id, 'user-unit-verification-failed', 'user unit verification failed')
                        } else {
                            await sendNotification(inMoment, unit?.attributes.User?.data?.id ?? 0, unit?.id, 'user-unit-rejected', 'user unit rejected')
                        }
                        const swalResult = await swalSuccess('Unit has been successfully rejected');
                        if (swalResult.isConfirmed) {
                            navigate(`/landlord/modalView/unit_modal/${unit?.id}/not/other_agent_s_unit`);
                        }
                    } else {
                        const swalResult = await swalError(textNotification);
                        if (swalResult.isConfirmed) {
                            navigate(`/landlord/modalView/unit_modal/${unit?.id}/not/other_agent_s_unit`);
                        }
                    }
                    dispatch(setWhereAfterToGo(`/landlord/my_units`))
                }
            }
        }).catch(console.log);
    }

    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const [footerPos, setFooterPos] = React.useState<{ left: number; width: number } | null>(null);
    const footerPosRef = React.useRef<{ left: number; width: number } | null>(null);

    React.useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        const update = () => {
            const r = el.getBoundingClientRect();
            const next = { left: Math.round(r.left), width: Math.round(r.width) };
            if (footerPosRef.current?.left === next.left && footerPosRef.current?.width === next.width) return;
            footerPosRef.current = next;
            setFooterPos(next);
        };

        update();

        const ro = new ResizeObserver(update);
        ro.observe(el);

        window.addEventListener("resize", update);

        return () => {
            ro.disconnect();
            window.removeEventListener("resize", update);
        };
    }, []);

    const activeUnitId = currentUnit?.id ?? unitID ?? '-'



    return (
        <>
            <div key={modalID} className="page d-flex flex-column flex-row-fluid">
                <div
                    ref={containerRef}
                    className={isMobileCanvas ? "" : "w-100 bg-white h-auto d-flex flex-center shadow-sm w-100 mw-600px mx-auto card shadow-sm border-0"}
                >

                    <div className="card d-flex mh-100 w-100 mx-auto shadow-sm border-0">
                        {modalID !== 'client_modal'
                            ? currentUnit != null
                                ? <ImagesPreviewGroup currentUnit={currentUnit} isDesktop={isDesktop} />
                                : null
                            : <div className="card-header shadow-sm">
                                <div className="card-title">
                                    <h3>Client preview</h3>
                                </div>
                            </div>
                        }

                        <div className="card-body px-5 h-50" style={{ paddingBottom: 140 }}>
                            {
                                modalID === 'viewing_modal' || modalID === 'magic_viewing_modal'
                                    ? currentUnit != null
                                        ? <ChildViewing key={`viewing - ${currentViewing?.[0]?.id} `} currentViewing={currentViewing} currentUnit={currentUnit} />
                                        : null
                                    : modalID === 'unit_modal'
                                        ? currentUnit != null
                                            ? <ChildUnit key={`unit - ${currentUnit?.id} `} currentUnit={currentUnit} />
                                            : null
                                        : modalID === 'offer_modal' || modalID === 'magic_offer_modal'
                                            ? currentUnit != null
                                                ? <ChildOffer key={`offer - ${currentOffer?.id} `} currentOffer={currentOfferRaw as IOffer} currentUnit={currentUnit} />
                                                : null
                                            : modalID === 'advertise_modal' || modalID === 'magic_advertise_modal'
                                                ? (currentUnit != null && currentAdvertise != null)
                                                    ? <ChildAdvertise key={`advertise - ${currentAdvertise?.id} `} currentAdvertise={currentAdvertise} currentUnit={currentUnit} />
                                                    : null
                                                : modalID === 'broadcast_modal'
                                                    ? currentUnit != null
                                                        ? <ChildBroadcast key={`broadcast - ${objectID} `} broadcastID={objectID as string} currentUnit={currentUnit} />
                                                        : null
                                                    : null
                            }

                            {
                                firstDataSource != null && firstModal != null
                                    ? <>
                                        <FormListForMyCustomForView
                                            form={firstModal?.[0]?.form}
                                            response={firstDataSource}
                                        />
                                        <div className="fv-row p-2 w-100">
                                            <div className="separator border border-bso border-3 px-2 my-4"></div>
                                        </div>
                                    </>
                                    : null
                            }

                            {modalID !== 'client_modal'
                                ? <>
                                    {modalID === "offer_modal" || modalID === "viewing_modal" || modalID === "advertise_modal" ?
                                        <div className="container  m-0 py-1 w-100 my-2 "
                                            onClick={
                                                modalID === "offer_modal" ?
                                                    () => (
                                                        dispatch(openChatByUserID(navigateToChatMain(currentOfferRaw, "forOffers").redirect)))
                                                        .then(({ id }: { id: any }) => {
                                                            if (id != null) {
                                                                navigate(`/${userTypeLowercase}/chats/${String(id)}`)
                                                            }
                                                        })
                                                    : modalID === "viewing_modal" ?
                                                        () => (
                                                            dispatch(openChatByUserID(navigateToChatMain(currentViewing[0], "forViewings").redirect)))
                                                            .then(({ id }: { id: any }) => {
                                                                if (id != null) {
                                                                    navigate(`/${userTypeLowercase}/chats/${String(id)}`)
                                                                }
                                                            })

                                                        : () => (
                                                            dispatch(openChatByUserID(navigateToChatMain(currentAdvertise, "forAdvertise").redirect)))
                                                            .then(({ id }: { id: any }) => {
                                                                if (id != null) {
                                                                    navigate(`/${userTypeLowercase}/chats/${String(id)}`)
                                                                }
                                                            })

                                            }

                                        >
                                            <div className="row mt-2">
                                                <div className="col-3 btn btn-flex btn-bso max-w-100px opacity-75  mx-auto">
                                                    <i className="fs-7x fs-lg-4x las la-sms"></i>
                                                </div>
                                                <div className="col-9 d-flex align-items-center bg-light-secondary">
                                                    <span className="d-flex flex-column align-items-start justify-content-center mx-2">
                                                        <span className="fs-3  fw-bold">
                                                            Chat
                                                        </span>
                                                        <span className="fs-7 lh-sm">Connect with {
                                                            modalID === "offer_modal" ? navigateToChatMain(currentOfferRaw, "forOffers").redirectName : modalID === "advertise_modal" ? navigateToChatMain(currentAdvertise, "forAdvertise").redirectName : navigateToChatMain(currentViewing[0], "forViewings").redirectName
                                                        }  <i className="las la-angle-right"></i>
                                                        </span>
                                                    </span>
                                                </div>
                                            </div>
                                        </div> : null
                                    }
                                    {!blockUnitActions && <TakeUnitsPortfolio unitID={currentUnit?.id} />}
                                    <GeoForMyCustomModalForView googleMapsLink={googleMapsLink} />
                                    {currentUnit != null && <VideoForMyCustomModalForView currentUnit={currentUnit} />}
                                    {currentUnit != null && <FloorPlanForMyCustomModalForView currentUnit={currentUnit} />}
                                    <div className="fv-row mb-2 m-0 p-2 w-100">
                                        <div className="separator border border-bso border-3 px-2 my-4"></div>
                                    </div>
                                </>
                                : <></>
                            }

                            <FormListForMyCustomForView
                                form={

                                    ['advertise_modal', 'magic_advertise_modal', 'offer_modal', 'magic_offer_modal', 'viewing_modal', 'magic_viewing_modal', 'broadcast_modal'].includes(modal?.[0]?.id)
                                        ? unitModalForm
                                        : modal?.[0]?.id === 'unit_modal'
                                            ? unitModalForm
                                            : modal?.[0]?.form
                                }
                                response={dataSource}
                            />
                            {modalID === 'unit_modal'
                                ? <>
                                    <div className="d-flex flex-column justify-content-end">
                                        <div className="text-gray-900 fs-6 me-0 mb-2">
                                            <CustomModalForViewRenderDescription
                                                handleClick={() => { }}
                                                marketing={currentUnit?.attributes?.Marketing ?? undefined}
                                                expanded={true}
                                            />
                                        </div>
                                    </div>
                                </>
                                : <></>
                            }
                            {modalID === 'unit_modal' && (
                                <AdvertiseParticipation
                                    mode="unit"
                                    currentUnit={currentUnit}
                                    currentAdvertise={currentAdvertise}
                                    advertises={advertises}
                                    allUsers={allUsers}
                                />
                            )}
                            {modalID === 'magic_advertise_modal' && (
                                <AdvertiseParticipation
                                    mode="magic"
                                    currentUnit={currentUnit}
                                    currentAdvertise={currentAdvertise}
                                    advertises={advertises}
                                    allUsers={allUsers}
                                />
                            )}

                            {currentUnit?.attributes?.ManagedBy !== 'BSO'
                                ? <AgentInfoForChildren userID={currentUnit?.attributes?.User?.data?.id ?? -1} />
                                : <div className="fv-row mb-2 m-0 p-2 w-100">
                                    <div className="separator border border-bso border-3 px-2 my-4"></div>
                                </div>}
                            {currentUnit != null && (currentUnit?.attributes.ManagedBy === 'Landlord' ?
                                <VerifiedUnitInformation currentUnit={currentUnit} /> :
                                <RegulatoryInformation currentUnit={currentUnit} />)

                            }
                            {(isReviewRequired || isPublishedServiceManagementFlow) && (isLandlordVerificationFlow || isAgentVerificationFlow) &&
                                <UnitVerificationReviewPanel
                                    currentUnit={verificationUnit}
                                    mode={isLandlordVerificationFlow ? 'landlord' : 'agent'}
                                    relatedOrders={relatedOrdersForCurrentUnit}
                                    onReadyChange={setIsVerificationReviewReady}
                                    flowType={isPublishedServiceManagementFlow ? 'published' : 'request'}
                                />
                            }
                            <div className='my-4' style={{ height: '90px' }}></div>
                        </div>


                        {
                            !previewVisible ?

                                <div
                                    className="footer bg-white shadow-sm  btn-fixed-in-footer  mw-600px "
                                    style={{
                                        position: "fixed",
                                        bottom: 0,
                                        left: footerPos?.left ?? 0,
                                        width: footerPos?.width ?? "100%",
                                        zIndex: 999,
                                    }}
                                >


                                    <div className={`d-flex flex-row w-100 h-100  p-2 ${userDevice === 'IPhone' ? 'pb-8' : 'pb-4'}  `} >
                                        {loading
                                            ? (
                                                <button className={'btn btn-bso mt-2 mb-6 me-2 w-100  border-bso2px p-4 py-5 w-100 mx-1 lh-1 '} >
                                                    <span className="indicator-progress d-block">
                                                        Please wait...
                                                        <span className={'spinner-border spinner-border-sm align-middle ms-4'}></span>
                                                    </span>
                                                </button>
                                            )
                                            : null}

                                        {!loading && modalID === 'unit_modal' && isMyUnit && <div
                                            className={`btn btn-bso mt-2 border-bso2px py-4 px-2 w-100 mx-1 ${userDevice === 'IPhone' ? 'mb-6' : ''} `}
                                            onClick={
                                                () => {
                                                    fetchAdvertisesForUnitForAdmin(Number(unitID))

                                                }}
                                        >
                                            <span className="indicator-label">
                                                Get Advertisements History
                                            </span>
                                        </div >}



                                        {!loading && (showButtonGoToActions) && !isPublishedUnitAdminFlow &&
                                            (<div
                                                className={`btn btn-bso mt-2 border-bso2px py-4 px-2 w-100 mx-1 ${userDevice === 'IPhone' ? 'mb-6' : ''}  ${blockUnitActions ? 'opacity-50' : 'opacity-100'}`}
                                                onClick={
                                                    () => {
                                                        navigate(
                                                            modalID?.includes('offer_modal')
                                                                ? `/${userTypeLowercase}/modal/offer_actions_from_agent/${activeUnitId}/${objectID}`
                                                                : `/${userTypeLowercase}/modal/viewing_actions_from_agent/${activeUnitId}/${objectID}`)

                                                    }}
                                            >
                                                <span className="indicator-label">
                                                    Go to Actions
                                                </span>
                                            </div >
                                            )
                                        }




                                        {!loading && isLandlordRegisteredForVerify ? <>
                                            <button
                                                className={`btn btn-bso mt-2 ${userDevice === 'IPhone' ? 'mb-6' : ''} border-bso2px p-4 py-5 w-100 mx-1 lh-1 d-flex align-items-center justify-content-center ${isReviewRequired && !isVerificationReviewReady ? 'opacity-50' : ''}`}
                                                disabled={isReviewRequired && !isVerificationReviewReady}
                                                onClick={() => {
                                                    if (isReviewRequired && !isVerificationReviewReady) return
                                                    void handleApproveRequestedUnit()
                                                }}
                                            >
                                                <span className="indicator-label">
                                                    Verify
                                                </span>
                                            </button>
                                            <button
                                                className={`btn btn-bso mt-2 ${userDevice === 'IPhone' ? 'mb-6' : ''} border-bso2px p-4 py-5 w-100 mx-1 lh-1 d-flex align-items-center justify-content-center ${isReviewRequired && !isVerificationReviewReady ? 'opacity-50' : ''}`}
                                                disabled={isReviewRequired && !isVerificationReviewReady}
                                                onClick={() => {
                                                    if (isReviewRequired && !isVerificationReviewReady) return
                                                    void handleRejectRequestedUnit()
                                                }}
                                            >
                                                <span className="indicator-label">
                                                    Reject
                                                </span>
                                            </button>
                                        </>
                                            : null
                                        }

                                        {!loading && isLandlordApprovedForPublish ? (
                                            <button
                                                className={`btn btn-bso mt-2 ${userDevice === 'IPhone' ? 'mb-6' : ''} border-bso2px p-4 py-5 w-100 mx-1 lh-1 d-flex align-items-center justify-content-center`}
                                                onClick={() => {
                                                    void handlePublishApprovedLandlordUnit()
                                                }}
                                            >
                                                <span className="indicator-label">
                                                    Publish to app catalog
                                                </span>
                                            </button>
                                        ) : null}

                                        {!loading && isAgentRegisteredReviewFlow ? <>
                                            <button
                                                className={`btn btn-bso mt-2 ${userDevice === 'IPhone' ? 'mb-6' : ''} border-bso2px p-4 py-5 w-100 mx-1 lh-1 d-flex align-items-center justify-content-center ${isReviewRequired && !isVerificationReviewReady ? 'opacity-50' : ''}`}
                                                disabled={isReviewRequired && !isVerificationReviewReady}
                                                onClick={() => {
                                                    if (isReviewRequired && !isVerificationReviewReady) return
                                                    void handleApproveRequestedUnit()
                                                }}
                                            >
                                                <span className="indicator-label">
                                                    Verify / Publish to app catalog
                                                </span>
                                            </button>
                                            <button
                                                className={`btn btn-bso mt-2 ${userDevice === 'IPhone' ? 'mb-6' : ''} border-bso2px p-4 py-5 w-100 mx-1 lh-1 d-flex align-items-center justify-content-center ${isReviewRequired && !isVerificationReviewReady ? 'opacity-50' : ''}`}
                                                disabled={isReviewRequired && !isVerificationReviewReady}
                                                onClick={() => {
                                                    if (isReviewRequired && !isVerificationReviewReady) return
                                                    void handleRejectRequestedUnit()
                                                }}
                                            >
                                                <span className="indicator-label">
                                                    Reject
                                                </span>
                                            </button>
                                        </>
                                            : null
                                        }






                                        {!loading && isShowFooter
                                            ? modal?.[0]?.footer?.map((row: any) => {
                                                const isPublishedHiddenAction = isPublishedUnitAdminFlow && (row?.id === 'actions' || row?.id === 'offer_action')
                                                if (isPublishedHiddenAction) return null
                                                return (
                                                    <React.Fragment key={`footer_action_${String(row?.id)}`}>
                                                        {
                                                            row.element === 'button-primary' && row.id === 'actions' && (
                                                                <Link
                                                                    to={`${`/landlord/${actionRoutes[modal?.[0]?.id] ?? ''}/${unitID ?? '-'}/${objectID ?? '-'}`}`}
                                                                    className={`btn btn-bso mt-2 border-bso2px py-4 px-2 w-100 mx-1 ${userDevice === 'IPhone' ? 'mb-6' : ''}  `}
                                                                >
                                                                    <div>
                                                                        <span className="indicator-label">{row?.content![0]}</span>
                                                                    </div>
                                                                </Link>
                                                            )
                                                        }


                                                        {/* {
                                                row.id === 'offer_action' &&
                                                offerReactRequireStatuses?.includes(statusesOffers?.[0]) &&
                                                !isShowBtnsForAgentsUnit && !currentUnit?.attributes?.Statuses?.includes('Door close') &&
                                                <CustomButton
                                                    userDevice={userDevice}
                                                    rowContent={"Take action"}
                                                    onClick={() => {
                                                        onCheckVerifiedStatusAndGoToAction(() => {
                                                            void processOfferSwal(currentOfferRaw as IOffer, dispatch, navigate)
                                                        })
                                                    }}
                                                />
                                            } */}
                                                    </React.Fragment>
                                                )
                                            }

                                            )
                                            : null}
                                    </div >
                                </div > : null
                        }
                    </div>


                    {/* </div> */}

                </div>


                {btnClose && !loading && !previewVisible &&
                    <CloseIconBasic key={`icon - ${modalID as string} `}
                        onCancel={() => {
                            modalID === 'advertise_modal' || modalID === 'viewing_modal' || modalID === 'offer_modal' || modalID === 'unit_modal' || modalID === 'broadcast_modal' ?
                                from === 'tasks' || from === 'calendar'
                                    ? navigate(`/${userTypeLowercase}/schedule`)
                                    : from === 'create'
                                        ? navigate(`/${userTypeLowercase}/units/nav`)
                                        : from === 'today'
                                            ? navigate(`/${userTypeLowercase}/dashboard`)
                                            : navigate(satedRedirect || btnClose.redirect || -1) // satedRedirect || -1)
                                : navigate(satedRedirect || btnClose.redirect || -1)  // btnClose.redirect ? btnClose.redirect : satedRedirect || -1)
                        }} cls='30px' />
                }

            </div >
        </>
    )
}
